import type { CellRowType } from '@mss_creator/shared';

/**
 * The current selection scope on the matrix generation page (decision D7,
 * 2026-08-06 drawer design). Ephemeral, client-only React state — never
 * persisted to `matrices.viewState` (decision D9) and reset to `matrix` on
 * every reload.
 *
 * `cells` is the multi-select scope added in session 4 (M11): an arbitrary
 * set of cell ids, produced only while multi-select mode is active. It is a
 * peer of the other scopes rather than a separate piece of state, so the
 * drawer's Historique/Régénérer tabs resolve it through the same
 * "which cells are in scope" path as everything else (M8).
 *
 * `activationBlock` (Task 4) targets the shared activation header's "..."
 * menu — the 6 activation sub-rows have no per-levier scope left to select
 * (session 5, M3), so this is the one grid-selectable scope that isn't tied
 * to a single row/column/cell.
 */
export type Selection =
  | { scope: 'matrix' }
  | { scope: 'column'; columnNumber: number }
  | { scope: 'row'; rowType: CellRowType; rowRef: string }
  | { scope: 'cell'; cellId: string }
  | { scope: 'cells'; cellIds: string[] }
  | { scope: 'activationBlock' };

export const MATRIX_SELECTION: Selection = { scope: 'matrix' };

/**
 * Which drawer tab a "..." menu item or a notes marker targets (M7).
 *
 * Three since issue #19. `contexte` used to sit here as the odd one out —
 * matrix-scoped, never the target of a "..." menu, ignoring the selection
 * entirely — which is exactly why it did not belong in a 384px panel built
 * around the grid selection in the first place. Its editor moved to the
 * full-width Cadrage tab (`?vue=cadrage`). `notes` is selection-scoped like
 * the two others: the notes of the selected cell, row or column, or of the
 * whole matrix when nothing is selected.
 */
export type DrawerTab = 'historique' | 'regen' | 'notes';

/**
 * What a grid "..." menu emits (M7): select this thing, open the drawer if
 * it's closed, and switch to this tab. The grid never opens the drawer
 * itself — it reports intent and the route owns both pieces of state.
 */
export interface DrawerRequest {
  selection: Selection;
  tab: DrawerTab;
}

/** Toggles `cellId` in/out of a multi-select set, preserving order. */
export function toggleCellInSelection(current: Selection, cellId: string): Selection {
  const existing = current.scope === 'cells' ? current.cellIds : [];
  const next = existing.includes(cellId)
    ? existing.filter((id) => id !== cellId)
    : [...existing, cellId];
  return next.length > 0 ? { scope: 'cells', cellIds: next } : MATRIX_SELECTION;
}

/**
 * Cells in scope for a given selection (D1/D2 — flat list, no rollup; M11
 * adds `cells`). Shared between the drawer's Historique tab and the route's
 * regeneration-badge computation (2026-08-14 design doc), which both need
 * "which cells does this selection cover".
 */
export function cellsInScope<
  T extends { id: string; columnNumber: number; rowType: CellRowType; rowRef: string },
>(selection: Selection, cells: T[]): T[] {
  switch (selection.scope) {
    case 'matrix':
      return cells;
    case 'column':
      return cells.filter((cell) => cell.columnNumber === selection.columnNumber);
    case 'row':
      return cells.filter(
        (cell) => cell.rowType === selection.rowType && cell.rowRef === selection.rowRef,
      );
    case 'cell':
      return cells.filter((cell) => cell.id === selection.cellId);
    case 'cells':
      return cells.filter((cell) => selection.cellIds.includes(cell.id));
    case 'activationBlock':
      return cells.filter((cell) => cell.rowType === 'activation');
    default:
      return [];
  }
}
